<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="title1" />

        <div class="card">
          <div class="card-body">
            <div v-if="loading" class="state-wrap">Loading user form...</div>
            <div v-else>
              <div v-if="errorMessage" class="alert alert-danger">{{ errorMessage }}</div>

              <form @submit.prevent="submitForm" class="access-form">
                <div class="row g-3">
                  <div class="col-lg-4 col-sm-6 col-12">
                    <label class="form-label">Name</label>
                    <input v-model="form.name" type="text" class="form-control" required />
                  </div>
                  <div class="col-lg-4 col-sm-6 col-12">
                    <label class="form-label">Email</label>
                    <input v-model="form.email" type="email" class="form-control" required />
                  </div>
                  <div class="col-lg-4 col-sm-6 col-12">
                    <label class="form-label">Role</label>
                    <select v-model="form.roleid" class="form-control" required>
                      <option value="">Select role</option>
                      <option v-for="role in activeRoles" :key="role.roleid" :value="role.roleid">
                        {{ role.rolename }}
                      </option>
                    </select>
                    <small class="form-text text-muted">
                      Choosing a role auto-applies that role's permissions.
                    </small>
                  </div>
                  <div class="col-lg-4 col-sm-6 col-12">
                    <label class="form-label">{{ isEdit ? 'New Password' : 'Password' }}</label>
                    <input
                      v-model="form.password"
                      :required="!isEdit"
                      type="password"
                      class="form-control"
                      :placeholder="isEdit ? 'Leave blank to keep current password' : 'Enter password'"
                    />
                  </div>
                  <div class="col-lg-4 col-sm-6 col-12">
                    <label class="form-label">Confirm Password</label>
                    <input
                      v-model="form.confirmPassword"
                      :required="!isEdit || !!form.password"
                      type="password"
                      class="form-control"
                    />
                  </div>
                  <div class="col-lg-2 col-sm-6 col-12">
                    <label class="form-label d-block">Active</label>
                    <input v-model="form.is_active" type="checkbox" />
                  </div>
                  <div class="col-lg-2 col-sm-6 col-12">
                    <label class="form-label d-block">Force Password Change</label>
                    <input v-model="form.must_change_password" type="checkbox" />
                  </div>
                </div>

                <div class="access-panel">
                  <div class="panel-head">
                    <div>
                      <h5>Location Access</h5>
                      <p>
                        Choose which locations this user can access and which one should open as the default session location.
                      </p>
                    </div>
                  </div>

                  <div class="row g-3">
                    <div class="col-lg-7 col-12">
                      <label class="form-label">Accessible Locations</label>
                      <div v-if="activeLocations.length" class="location-access-grid">
                        <label
                          v-for="location in activeLocations"
                          :key="location.locationid"
                          class="location-access-card"
                        >
                          <input
                            v-model="form.locationids"
                            type="checkbox"
                            :value="location.locationid"
                          />
                          <div>
                            <strong>{{ location.locationname }}</strong>
                            <span>{{ location.locationid }}</span>
                          </div>
                        </label>
                      </div>
                      <div v-else class="empty-hint">
                        No active locations are available yet.
                      </div>
                    </div>

                    <div class="col-lg-5 col-12">
                      <label class="form-label">Default Location</label>
                      <select v-model="form.locationid" class="form-control" :disabled="!selectedLocationOptions.length">
                        <option value="">Select default location</option>
                        <option
                          v-for="location in selectedLocationOptions"
                          :key="location.locationid"
                          :value="location.locationid"
                        >
                          {{ location.locationname }}
                        </option>
                      </select>
                      <small class="form-text text-muted">
                        The default location must be one of the selected accessible locations.
                      </small>

                      <div class="location-summary">
                        <span class="summary-pill role-pill">Selected: {{ form.locationids.length }}</span>
                        <span class="summary-pill grant-pill">Default: {{ defaultLocationLabel }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="access-panel">
                  <div class="panel-head">
                    <div>
                      <h5>User Permissions</h5>
                      <p>
                        Role permissions are auto-selected. You can still add or remove permissions for this specific user.
                      </p>
                    </div>
                    <button
                      v-if="selectedRole"
                      type="button"
                      class="btn btn-outline-secondary btn-sm"
                      @click="applyRolePermissions"
                    >
                      Reset To Role
                    </button>
                  </div>

                  <div v-if="selectedRole" class="override-summary">
                    <span class="summary-pill role-pill">Role: {{ selectedRole.rolename }}</span>
                    <span class="summary-pill grant-pill">Extra grants: {{ extraGrantCount }}</span>
                    <span class="summary-pill deny-pill">Removed from role: {{ explicitDenyCount }}</span>
                  </div>

                  <PermissionMatrix
                    v-model="effectivePermissionIds"
                    :permissions="permissionGroups"
                    title="Permissions"
                  />
                </div>

                <div class="form-actions">
                  <button class="btn btn-submit" type="submit" :disabled="submitting">
                    {{ submitting ? 'Saving...' : (isEdit ? 'Update User' : 'Create User') }}
                  </button>
                  <router-link class="btn btn-cancel" to="/userlists">Cancel</router-link>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

import PermissionMatrix from '@/components/access/PermissionMatrix.vue';
import { buildOverrides, groupPermissionsByModule } from '@/utils/accessControl';

export default {
  name: 'UserFormPage',
  components: { PermissionMatrix },
  props: {
    mode: {
      type: String,
      default: 'create',
    },
  },
  data() {
    return {
      loading: true,
      submitting: false,
      errorMessage: '',
      roles: [],
      locations: [],
      permissions: [],
      effectivePermissionIds: [],
      form: {
        name: '',
        email: '',
        roleid: '',
        locationid: '',
        locationids: [],
        password: '',
        confirmPassword: '',
        is_active: true,
        must_change_password: true,
      },
    };
  },
  computed: {
    isEdit() {
      return this.mode === 'edit';
    },
    userId() {
      return this.$route.query.id || '';
    },
    title() {
      return this.isEdit ? 'Edit User' : 'Create User';
    },
    title1() {
      return this.isEdit ? 'Update role and permission overrides' : 'Create a user with role and permission overrides';
    },
    activeRoles() {
      return this.roles.filter((role) => role.is_active);
    },
    activeLocations() {
      return this.locations.filter((location) => location.is_active);
    },
    selectedLocationOptions() {
      const selectedIds = new Set((this.form.locationids || []).map(String));
      return this.activeLocations.filter((location) => selectedIds.has(String(location.locationid)));
    },
    defaultLocationLabel() {
      const match = this.selectedLocationOptions.find((location) => location.locationid === this.form.locationid);
      return match ? match.locationname : 'Not set';
    },
    permissionGroups() {
      return groupPermissionsByModule(this.permissions);
    },
    selectedRole() {
      return this.roles.find((role) => String(role.roleid) === String(this.form.roleid)) || null;
    },
    selectedRolePermissionIds() {
      return (this.selectedRole?.permissions || []).map((permission) => String(permission.permissionid));
    },
    selectedRolePermissionIdSet() {
      return new Set(this.selectedRolePermissionIds);
    },
    effectivePermissionIdSet() {
      return new Set((this.effectivePermissionIds || []).map(String));
    },
    extraGrantCount() {
      return this.effectivePermissionIds.filter((id) => !this.selectedRolePermissionIdSet.has(String(id))).length;
    },
    explicitDenyCount() {
      return this.selectedRolePermissionIds.filter((id) => !this.effectivePermissionIdSet.has(String(id))).length;
    },
  },
  watch: {
    'form.roleid'(next, prev) {
      if (!next) {
        this.effectivePermissionIds = [];
        return;
      }

      if (!this.loading && next !== prev) {
        this.applyRolePermissions();
      }
    },
    'form.locationids': {
      handler(next) {
        const locationIds = Array.isArray(next) ? next.map(String) : [];
        const deduped = [...new Set(locationIds)];

        if (deduped.length !== locationIds.length) {
          this.form.locationids = deduped;
          return;
        }

        if (!deduped.length) {
          this.form.locationid = '';
          return;
        }

        if (!deduped.includes(this.form.locationid)) {
          this.form.locationid = deduped[0];
        }
      },
      deep: true,
    },
  },
  methods: {
    permissionIdsFromKeys(keys = []) {
      const keySet = new Set(keys);
      return this.permissions
        .filter((permission) => keySet.has(permission.permission_key))
        .map((permission) => String(permission.permissionid));
    },
    applyRolePermissions() {
      this.effectivePermissionIds = [...this.selectedRolePermissionIds];
    },
    async loadData() {
      this.loading = true;
      this.errorMessage = '';

      try {
        const requests = [
          axios.get('/v1/access/roles'),
          axios.get('/v1/access/permissions'),
          axios.get('/v1/locations'),
        ];

        if (this.isEdit && this.userId) {
          requests.push(axios.get(`/v1/users/${this.userId}`));
        }

        const [rolesRes, permissionsRes, locationsRes, userRes] = await Promise.all(requests);
        this.roles = rolesRes.data || [];
        this.permissions = permissionsRes.data || [];
        this.locations = locationsRes.data || [];

        if (userRes?.data) {
          const user = userRes.data;
          this.form = {
            name: user.name || '',
            email: user.email || '',
            roleid: user.role?.roleid || '',
            locationid: user.location?.locationid || '',
            locationids: (user.locations || []).map((location) => location.locationid),
            password: '',
            confirmPassword: '',
            is_active: !!user.is_active,
            must_change_password: !!user.must_change_password,
          };
          this.effectivePermissionIds = this.permissionIdsFromKeys(user.effective_permissions || []);
        } else if (this.form.roleid) {
          this.applyRolePermissions();
        }
      } catch (error) {
        this.errorMessage = error.response?.data?.detail || 'Failed to load user form.';
      } finally {
        this.loading = false;
      }
    },
    async submitForm() {
      if (this.form.password !== this.form.confirmPassword) {
        Swal.fire({ icon: 'error', title: 'Mismatch', text: 'Password and confirm password must match.' });
        return;
      }

      if (!this.form.locationids.length) {
        Swal.fire({ icon: 'error', title: 'Location required', text: 'Select at least one accessible location for this user.' });
        return;
      }

      if (!this.form.locationid || !this.form.locationids.includes(this.form.locationid)) {
        Swal.fire({ icon: 'error', title: 'Default required', text: 'Choose a default location from the selected accessible locations.' });
        return;
      }

      this.submitting = true;
      try {
        const selectedSet = new Set((this.effectivePermissionIds || []).map(String));
        const roleSet = new Set(this.selectedRolePermissionIds.map(String));
        const grantedPermissions = [...selectedSet].filter((id) => !roleSet.has(id));
        const deniedPermissions = [...roleSet].filter((id) => !selectedSet.has(id));

        const payload = {
          name: this.form.name,
          email: this.form.email,
          roleid: this.form.roleid || null,
          locationid: this.form.locationid || null,
          locationids: [...this.form.locationids],
          is_active: this.form.is_active,
          must_change_password: this.form.must_change_password,
          permission_overrides: buildOverrides(grantedPermissions, deniedPermissions),
        };

        if (this.form.password) {
          payload.password = this.form.password;
        }

        if (this.isEdit) {
          await axios.put(`/v1/users/${this.userId}`, payload);
        } else {
          await axios.post('/v1/users', payload);
        }

        Swal.fire({
          icon: 'success',
          title: this.isEdit ? 'User updated' : 'User created',
          timer: 1400,
          showConfirmButton: false,
        });
        this.$router.push('/userlists');
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Save failed',
          text: error.response?.data?.detail || 'Could not save user.',
        });
      } finally {
        this.submitting = false;
      }
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>

<style scoped>
.state-wrap { padding:24px; color:#6b7280; }
.access-form { display:flex; flex-direction:column; gap:20px; }
.access-panels { display:grid; grid-template-columns:repeat(auto-fit, minmax(320px, 1fr)); gap:20px; margin-top:8px; }
.access-panel { border:1px solid #e5e7eb; border-radius:14px; padding:16px; background:#fff; }
.panel-head { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; margin-bottom:12px; flex-wrap:wrap; }
.panel-head h5 { margin:0 0 4px; font-size:1rem; }
.panel-head p { margin:0; color:#6b7280; font-size:.88rem; }
.override-summary { display:flex; gap:10px; flex-wrap:wrap; }
.summary-pill { display:inline-flex; align-items:center; padding:6px 10px; border-radius:999px; font-size:.82rem; font-weight:600; }
.role-pill { background:#eff6ff; color:#1d4ed8; border:1px solid #93c5fd; }
.grant-pill { background:#ecfdf5; color:#166534; border:1px solid #86efac; }
.deny-pill { background:#fef2f2; color:#991b1b; border:1px solid #fca5a5; }
.location-access-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(220px, 1fr)); gap:12px; }
.location-access-card { display:flex; align-items:flex-start; gap:10px; border:1px solid #e5e7eb; border-radius:12px; padding:12px; background:#f9fafb; cursor:pointer; min-height:74px; }
.location-access-card input { margin-top:4px; }
.location-access-card strong { display:block; color:#111827; }
.location-access-card span { display:block; color:#6b7280; font-size:.85rem; margin-top:4px; }
.location-summary { display:flex; gap:10px; flex-wrap:wrap; margin-top:14px; }
.empty-hint { border:1px dashed #d1d5db; border-radius:12px; padding:18px; color:#6b7280; background:#f9fafb; }
.form-actions { display:flex; gap:12px; flex-wrap:wrap; margin-top:4px; }
</style>
